import { useEffect, useState, type FormEvent } from 'react';
import { Link, useNavigate } from '@tanstack/react-router';
import { useLogin } from '@/hooks/useAuth';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { ApiError } from '@/lib/api-client';
import { OAuthButtons } from '@/components/auth/OAuthButtons';
import { PublicShell } from '@/components/layout/PublicShell';

const oauthErrorMessages: Record<string, string> = {
  oauth_invalid_state: 'Güvenlik doğrulaması başarısız. Tekrar deneyin.',
  oauth_missing_params: 'OAuth yanıtı eksik parametre içerdi.',
  oauth_token_failed: 'Sağlayıcıdan token alınamadı.',
  oauth_no_token: 'Token yanıtı boş döndü.',
  oauth_userinfo_failed: 'Kullanıcı bilgileri alınamadı.',
  oauth_no_email: 'Sağlayıcıdan e-posta adresi alınamadı.',
  provider_not_configured: 'Bu giriş yöntemi henüz yapılandırılmadı.',
};

export function LoginPage() {
  const navigate = useNavigate();
  const login = useLogin();
  const [form, setForm] = useState({ email: '', password: '' });
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const err = params.get('error');
    if (err) {
      setError(oauthErrorMessages[err] ?? `Hata: ${err}`);
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, []);

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    try {
      await login.mutateAsync(form);
      navigate({ to: '/dashboard' });
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Bir hata oluştu');
    }
  };

  return (
    <PublicShell>
      <div className="container flex items-center justify-center py-12">
        <Card className="w-full max-w-md">
        <CardHeader>
          <CardTitle>Giriş Yap</CardTitle>
          <CardDescription>E-posta ve şifrenle giriş yap.</CardDescription>
        </CardHeader>
        <form onSubmit={onSubmit}>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="email">E-posta</Label>
              <Input
                id="email"
                type="email"
                required
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
              />
            </div>
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label htmlFor="password">Şifre</Label>
                <Link
                  to="/forgot-password"
                  className="text-xs text-muted-foreground hover:text-primary underline-offset-4 hover:underline"
                >
                  Şifremi unuttum
                </Link>
              </div>
              <Input
                id="password"
                type="password"
                required
                value={form.password}
                onChange={(e) => setForm({ ...form, password: e.target.value })}
              />
            </div>
            {error && <div className="text-sm text-destructive">{error}</div>}
          </CardContent>
          <CardFooter className="flex flex-col gap-4">
            <Button type="submit" className="w-full" disabled={login.isPending}>
              {login.isPending ? 'Giriş yapılıyor…' : 'Giriş Yap'}
            </Button>
            <OAuthButtons mode="login" />
            <div className="text-sm text-muted-foreground">
              Hesabın yok mu?{' '}
              <Link to="/register" className="text-primary underline-offset-4 hover:underline">
                Kayıt ol
              </Link>
            </div>
          </CardFooter>
        </form>
        </Card>
      </div>
    </PublicShell>
  );
}
